import React, { useState, useEffect } from 'react';
import { 
  Cloud, Sun, CloudRain, Wind, AlertTriangle, Snowflake,
  RefreshCw, ChevronDown, ChevronUp, Thermometer,
  Droplets, MapPin, CloudLightning, CloudFog
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WeatherAlerts = ({ refreshInterval = 300000 }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [apiConfigured, setApiConfigured] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchWeatherData = async (showToast = false) => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('monitoringAdminToken');
      if (!token) return;

      const statusRes = await fetch(`${API_URL}/api/monitoring/weather/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (statusRes.ok) {
        const statusData = await statusRes.json();
        if (!statusData.configured) {
          setApiConfigured(false);
          setLoading(false);
          setRefreshing(false);
          return;
        }
      }

      const response = await fetch(`${API_URL}/api/monitoring/weather/all-locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setWeatherData(data);
        setApiConfigured(true);
        setLastUpdate(new Date());
        
        if (showToast) {
          toast.success('Weather data refreshed');
        }
      } else if (response.status === 404) {
        setApiConfigured(true);
        setWeatherData(null);
      }

      const alertsRes = await fetch(`${API_URL}/api/monitoring/weather/alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (alertsRes.ok) {
        const alertsData = await alertsRes.json();
        setAlerts(alertsData.alerts || []);
      }

    } catch (error) {
      console.error('Error fetching weather:', error);
      if (showToast) {
        toast.error('Failed to fetch weather data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchWeatherData();
    const interval = setInterval(() => fetchWeatherData(), refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  // Get weather icon based on condition
  const getWeatherIcon = (condition, size = 5) => {
    const conditionLower = (condition || '').toLowerCase();
    const sizeClass = `h-${size} w-${size}`;
    
    if (conditionLower.includes('thunder') || conditionLower.includes('storm')) {
      return <CloudLightning className={`${sizeClass} text-purple-500`} />;
    }
    if (conditionLower.includes('rain') || conditionLower.includes('drizzle') || conditionLower.includes('shower')) {
      return <CloudRain className={`${sizeClass} text-blue-500`} />;
    }
    if (conditionLower.includes('snow') || conditionLower.includes('sleet') || conditionLower.includes('ice')) {
      return <Snowflake className={`${sizeClass} text-cyan-400`} />;
    }
    if (conditionLower.includes('fog') || conditionLower.includes('mist') || conditionLower.includes('haze')) {
      return <CloudFog className={`${sizeClass} text-gray-400`} />;
    }
    if (conditionLower.includes('cloud') || conditionLower.includes('overcast')) {
      return <Cloud className={`${sizeClass} text-gray-500`} />;
    }
    if (conditionLower.includes('sun') || conditionLower.includes('clear')) {
      return <Sun className={`${sizeClass} text-yellow-500`} />;
    }
    if (conditionLower.includes('wind')) {
      return <Wind className={`${sizeClass} text-teal-500`} />;
    }
    return <Cloud className={`${sizeClass} text-gray-400`} />;
  };

  // Get 3D card style based on weather condition and risk
  const getCard3DStyle = (condition, riskLevel) => {
    const conditionLower = (condition || '').toLowerCase();
    
    if (riskLevel === 'high') {
      return {
        background: 'linear-gradient(145deg, #fef2f2, #fee2e2)',
        border: '2px solid #fca5a5',
        boxShadow: '8px 8px 16px rgba(239, 68, 68, 0.2), -4px -4px 12px rgba(255, 255, 255, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.5)',
        transform: 'perspective(1000px) rotateX(2deg) rotateY(-2deg)'
      };
    }
    
    if (riskLevel === 'medium') {
      return {
        background: 'linear-gradient(145deg, #fffbeb, #fef3c7)',
        border: '2px solid #fcd34d',
        boxShadow: '8px 8px 16px rgba(245, 158, 11, 0.2), -4px -4px 12px rgba(255, 255, 255, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.5)',
        transform: 'perspective(1000px) rotateX(2deg) rotateY(-2deg)'
      };
    }
    
    if (conditionLower.includes('thunder') || conditionLower.includes('storm')) {
      return {
        background: 'linear-gradient(145deg, #f3e8ff, #e9d5ff)',
        border: '2px solid #c084fc',
        boxShadow: '8px 8px 16px rgba(147, 51, 234, 0.15), -4px -4px 12px rgba(255, 255, 255, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.5)',
        transform: 'perspective(1000px) rotateX(2deg) rotateY(-2deg)'
      };
    }
    
    if (conditionLower.includes('rain') || conditionLower.includes('drizzle') || conditionLower.includes('shower')) {
      return {
        background: 'linear-gradient(145deg, #eff6ff, #dbeafe)',
        border: '2px solid #93c5fd',
        boxShadow: '8px 8px 16px rgba(59, 130, 246, 0.15), -4px -4px 12px rgba(255, 255, 255, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.5)',
        transform: 'perspective(1000px) rotateX(2deg) rotateY(-2deg)'
      };
    }
    
    if (conditionLower.includes('snow') || conditionLower.includes('ice')) {
      return {
        background: 'linear-gradient(145deg, #ecfeff, #cffafe)',
        border: '2px solid #67e8f9',
        boxShadow: '8px 8px 16px rgba(6, 182, 212, 0.15), -4px -4px 12px rgba(255, 255, 255, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.5)',
        transform: 'perspective(1000px) rotateX(2deg) rotateY(-2deg)'
      };
    }
    
    if (conditionLower.includes('fog') || conditionLower.includes('mist') || conditionLower.includes('haze')) {
      return {
        background: 'linear-gradient(145deg, #f9fafb, #f3f4f6)',
        border: '2px solid #d1d5db',
        boxShadow: '8px 8px 16px rgba(107, 114, 128, 0.15), -4px -4px 12px rgba(255, 255, 255, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.5)',
        transform: 'perspective(1000px) rotateX(2deg) rotateY(-2deg)'
      };
    }
    
    if (conditionLower.includes('cloud') || conditionLower.includes('overcast')) {
      return {
        background: 'linear-gradient(145deg, #f8fafc, #e2e8f0)',
        border: '2px solid #94a3b8',
        boxShadow: '8px 8px 16px rgba(100, 116, 139, 0.15), -4px -4px 12px rgba(255, 255, 255, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.5)',
        transform: 'perspective(1000px) rotateX(2deg) rotateY(-2deg)'
      };
    }
    
    return {
      background: 'linear-gradient(145deg, #f0fdf4, #dcfce7)',
      border: '2px solid #86efac',
      boxShadow: '8px 8px 16px rgba(34, 197, 94, 0.15), -4px -4px 12px rgba(255, 255, 255, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.5)',
      transform: 'perspective(1000px) rotateX(2deg) rotateY(-2deg)'
    };
  };

  // Get risk badge with enhanced styling
  const getRiskBadge = (riskLevel) => {
    const styles = {
      high: 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md shadow-red-200',
      medium: 'bg-gradient-to-r from-amber-400 to-amber-500 text-white shadow-md shadow-amber-200',
      low: 'bg-gradient-to-r from-green-400 to-green-500 text-white shadow-md shadow-green-200'
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${styles[riskLevel] || styles.low}`}>
        {riskLevel?.toUpperCase()}
      </span>
    );
  };

  // Get display name for location
  const getLocationName = (loc) => {
    return loc.original_city || loc.location?.display_name || loc.location?.name || 'Unknown';
  };

  if (!apiConfigured) {
    return (
      <div className="rounded-xl overflow-hidden shadow-lg mb-4" data-testid="weather-alerts-card">
        {/* Header Bar - Orange/Amber for unconfigured */}
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Cloud className="h-6 w-6 text-white" />
              <div>
                <h3 className="text-white font-bold text-lg">Weather-Based Alerts</h3>
                <p className="text-amber-100 text-sm">Weather monitoring</p>
              </div>
            </div>
          </div>
        </div>
        {/* Content */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-2" />
          <p className="text-amber-800 font-medium">Weather API Not Configured</p>
          <p className="text-sm text-amber-600 mt-1">Add WEATHER_API_KEY to enable weather monitoring.</p>
          <p className="text-xs text-amber-500 mt-2">Get a free API key from weatherapi.com</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden shadow-lg mb-4" data-testid="weather-alerts-card">
      {/* Header Bar - Similar to Connectivity Alerts */}
      <div className="bg-gradient-to-r from-sky-500 via-blue-500 to-indigo-600 p-4">
        <div className="flex items-center justify-between">
          {/* Left side - Title and stats */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <Cloud className="h-6 w-6 text-white" />
              <div>
                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                  Weather-Based Alerts
                  {alerts.length > 0 && (
                    <span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-bold animate-pulse">
                      {alerts.length} Alert{alerts.length > 1 ? 's' : ''}
                    </span>
                  )}
                </h3>
                <p className="text-sky-100 text-sm">Real-time weather monitoring</p>
              </div>
            </div>
          </div>
          
          {/* Right side - Stats badges */}
          <div className="flex items-center gap-3">
            {/* Locations */}
            <div className="bg-sky-400/30 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-sky-300/50">
              <span className="text-white font-bold text-lg">{weatherData?.summary?.total_locations || 0}</span>
              <span className="text-sky-100 text-xs ml-1">Locations</span>
            </div>
            
            {/* High Risk */}
            <div className="bg-red-500/80 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-red-400/50 flex items-center gap-1">
              <AlertTriangle className="h-4 w-4 text-white" />
              <span className="text-white font-bold">{weatherData?.summary?.high_risk || 0}</span>
              <span className="text-red-100 text-xs">High</span>
            </div>
            
            {/* Medium Risk */}
            <div className="bg-amber-500/80 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-amber-400/50">
              <span className="text-white font-bold">{weatherData?.summary?.medium_risk || 0}</span>
              <span className="text-amber-100 text-xs ml-1">Medium</span>
            </div>
            
            {/* Low Risk */}
            <div className="bg-green-500/80 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-green-400/50">
              <span className="text-white font-bold">{weatherData?.summary?.low_risk || 0}</span>
              <span className="text-green-100 text-xs ml-1">Low</span>
            </div>
            
            {/* Last Update Time */}
            {lastUpdate && (
              <div className="text-sky-100 text-xs">
                {lastUpdate.toLocaleTimeString()}
              </div>
            )}
            
            {/* Expand/Collapse */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="h-8 w-8 p-0 text-white hover:bg-white/20"
            >
              {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      {expanded && (
        <div className="bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-sky-500" />
              <span className="ml-2 text-sky-600">Loading weather data...</span>
            </div>
          ) : (
            <>
              {/* Alerts Section */}
              {alerts.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" />
                    Active Weather Alerts
                  </h4>
                  <div className="space-y-2">
                    {alerts.map((alert, idx) => (
                      <div 
                        key={idx} 
                        className="rounded-xl p-3 transition-all duration-300 hover:scale-[1.02]"
                        style={getCard3DStyle(alert.condition, 'high')}
                        data-testid={`weather-alert-${idx}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            {getWeatherIcon(alert.condition, 6)}
                            <div>
                              <p className="font-bold text-red-800">{getLocationName(alert)}</p>
                              <p className="text-xs text-red-600">{alert.location?.region || alert.region}</p>
                            </div>
                          </div>
                          {getRiskBadge(alert.risk_level)}
                        </div>
                        <div className="mt-2 flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-red-700 font-medium">
                            <Thermometer className="h-4 w-4" />
                            {alert.current?.temp_c || alert.temp_c}°C
                          </span>
                          <span className="text-red-600">{alert.current?.condition || alert.condition}</span>
                        </div>
                        {alert.alert_message && (
                          <p className="mt-2 text-xs bg-red-100/80 text-red-700 p-2 rounded-lg font-medium">
                            {alert.alert_message}
                          </p>
                        )}
                        <p className="text-xs text-red-500 mt-1 font-medium">
                          {alert.customer_count} customer{alert.customer_count !== 1 ? 's' : ''} affected
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Location Weather Grid - 3D Cards */}
              {weatherData?.locations?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-sky-700 mb-3 flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    All Locations ({weatherData.locations.length})
                    <span className="ml-2 flex items-center gap-1 text-xs text-green-600">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      Real-time
                    </span>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-80 overflow-y-auto pr-1">
                    {weatherData.locations.map((loc, idx) => {
                      const cardStyle = getCard3DStyle(loc.current?.condition, loc.risk_level);
                      return (
                        <div 
                          key={idx}
                          className="rounded-xl p-3 transition-all duration-300 hover:scale-105 cursor-default relative"
                          style={cardStyle}
                          data-testid={`weather-location-${idx}`}
                        >
                          {/* LIVE indicator */}
                          <div className="absolute top-2 right-2 flex items-center gap-1">
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                            </span>
                            <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider">Live</span>
                          </div>
                          
                          <div className="flex items-center gap-2 mb-2 pr-12">
                            {getWeatherIcon(loc.current?.condition, 5)}
                            <span className="font-bold text-gray-800 text-sm truncate" title={getLocationName(loc)}>
                              {getLocationName(loc)}
                            </span>
                          </div>
                          
                          {/* Risk Badge */}
                          <div className="mb-2">
                            {getRiskBadge(loc.risk_level)}
                          </div>
                          
                          {/* Temperature display */}
                          <div className="text-center my-2">
                            <span className="text-3xl font-bold text-gray-800">
                              {loc.current?.temp_c}°
                            </span>
                            <span className="text-sm text-gray-600">C</span>
                          </div>
                          
                          {/* Weather details */}
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center gap-1 text-gray-600">
                              <Droplets className="h-3 w-3 text-blue-500" />
                              <span>{loc.current?.humidity}%</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-600">
                              <Wind className="h-3 w-3 text-teal-500" />
                              <span>{loc.current?.wind_kph} km/h</span>
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-500 truncate mt-2 text-center font-medium">
                            {loc.current?.condition}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No Data State */}
              {!weatherData?.locations?.length && !loading && (
                <div className="text-center py-6">
                  <Cloud className="h-12 w-12 text-sky-300 mx-auto mb-2" />
                  <p className="text-sky-700">No location weather data available</p>
                  <p className="text-xs text-sky-500 mt-1">
                    Add customer locations with city data to see weather info
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default WeatherAlerts;
